import { useContext, useCallback, useState } from 'react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { Context } from '@components/Context';
import { getFromLocalStorage, setOnLocalStorage } from '@utils/localStorage';
import { verifyCouponShared } from '@utils/sharedCouponVerification';

function useApplyCouponFromUrl() {
  const { state, dispatch } = useContext(Context);
  const { cartItems, country, currency, price, couponCode, discountAmount } = state;
  const { t } = useTranslation();
  const location = useLocation();
  const [isApplying, setIsApplying] = useState(false);

  // Function to apply coupon from URL/localStorage when cart is viewed
  const applyRefCodeIfPresent = useCallback(async () => {
    // Don't apply if we already have a coupon applied or if we're currently applying
    if (couponCode || isApplying) {
      return { success: false, reason: 'already_applied' };
    }

    // Check for URL refCode or localStorage
    const urlParams = new URLSearchParams(location.search);
    const refCodeParam = urlParams.get('refCode');
    const storedRefCode = getFromLocalStorage('link-referral-code');

    const couponToApply = refCodeParam || storedRefCode;

    if (!couponToApply) {
      return { success: false, reason: 'no_coupon' };
    }

    // Store refCode from URL to localStorage if it's new
    if (refCodeParam && refCodeParam !== storedRefCode) {
      setOnLocalStorage('link-referral-code', refCodeParam);
    }

    // Only proceed with coupon application if we have cart items
    if (cartItems.length === 0) {
      return { success: false, reason: 'empty_cart' };
    }

    setIsApplying(true);

    try {
      const result = await verifyCouponShared({
        coupon: couponToApply,
        cartItems,
        country,
        currency,
        price,
        t,
        location
      });

      if (result.success) {
        // Apply coupon to cart context
        dispatch({ type: 'setCouponCode', data: couponToApply });
        dispatch({ type: 'setCoupon', data: result.couponObject });
        dispatch({ type: 'setCouponError', data: null });

        // Apply discount amount
        if (result.discountAmount) {
          dispatch({ type: 'setDiscountAmount', data: result.discountAmount });
        }

        // Add free gift if applicable
        if (result.freeGift) {
          dispatch({ type: 'cartItemAdd', data: result.freeGift });
        }

        return { success: true, discount: result.discountAmount };
      }
      if (result.error) {
        // Set error but don't show it prominently in cart
        dispatch({ type: 'setCouponError', data: result.error });
        return { success: false, reason: 'verification_failed', error: result.error };
      }
    } catch (error) {
      dispatch({ type: 'setCouponError', data: 'Failed to apply coupon' });
      return { success: false, reason: 'error', error: error.message };
    } finally {
      setIsApplying(false);
    }

    return { success: false, reason: 'unknown' };
  }, [cartItems, country, currency, price, location.search, couponCode, dispatch, t, isApplying]);

  return {
    applyRefCodeIfPresent,
    isApplying,
    hasCouponApplied: !!couponCode,
    discountAmount: discountAmount || 0
  };
}

export default useApplyCouponFromUrl;
